import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './ApplyModal.module.css';
import { submitAuditionApplication } from '../../services/auditionService';
import { useAuth } from '../../contexts/AuthContext';
import ApplyModalBody from './ApplyModalBody';
import Icon from '../../components/common/Icon/Icon';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

function ApplyModalHeader({ onClose, auditionTitle }) {
  return (
    <div className={styles.header}>
      <div className={styles.headerTitleGroup}>
        <h2 id="apply-modal-title" className={styles.headerTitle}>공고 지원</h2>
        {auditionTitle && (
          <p className={styles.headerSubtitle}>{auditionTitle}에 지원합니다.</p>
        )}
      </div>
      <button type="button" className={styles.closeButton} onClick={onClose} aria-label="모달 닫기">
        <Icon name="close" size="20" />
      </button>
    </div>
  );
}

function ApplyModalFooter({ onClose, onSubmit, isSubmitting, isSubmitDisabled, isConsented, onConsentChange }) {
  return (
    <div className={styles.footer}>
      <div className={styles.consentRow}>
        <label className={styles.consentLabel}>
          <input
            type="checkbox"
            className={styles.consentCheck}
            checked={isConsented}
            onChange={(e) => onConsentChange(e.target.checked)}
          />
          <span className={styles.consentText}>
            위 내용을 확인하였으며, 프로필 정보 제공에 동의합니다.
          </span>
        </label>
      </div>
      <div className={styles.buttonsRow}>
        <button type="button" className={styles.cancelButton} onClick={onClose} disabled={isSubmitting}>
          취소
        </button>
        <button type="button" className={styles.submitButton} onClick={onSubmit} disabled={isSubmitDisabled || isSubmitting}>
          {isSubmitting ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <LoadingSpinner size="sm" style={{ width: 18, height: 18 }} />
              지원 중...
            </span>
          ) : '지원하기'}
        </button>
      </div>
    </div>
  );
}

function ApplyModal({ isOpen, onClose, auditionTitle, roles, initialRoleId, auditionId, onSuccess, myTalentInfo }) {
  const { profileId } = useAuth();
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [bio, setBio] = useState('');
  const [files, setFiles] = useState([]);
  const [videoLink, setVideoLink] = useState('');
  const [isConsented, setIsConsented] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);
  const [conditionAlert, setConditionAlert] = useState(null);

  const checkRoleConditions = (roleId) => {
    if (!myTalentInfo || !roleId) return true;
    const roleData = roles.find(r => String(r.id) === String(roleId));
    if (!roleData) return true;
    const { gender } = myTalentInfo;
    if (gender && roleData.gender && roleData.gender !== '무관' && roleData.gender !== 'ANY') {
      const genderMap = { MALE: '남성', FEMALE: '여성' };
      const myGenderKo = genderMap[gender] || gender;
      const roleGenderKo = genderMap[roleData.gender] || roleData.gender;
      if (roleData.gender !== myGenderKo && roleGenderKo !== myGenderKo) {
        setConditionAlert({
          title: '지원 불가',
          message: `이 배역은 ${roleGenderKo}만 지원 가능합니다.`,
        });
        return false;
      }
    }
    return true;
  };

  const handleRoleSelect = (roleId) => {
    if (!checkRoleConditions(roleId)) return;
    setSelectedRoleId(roleId);
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedRoleId(initialRoleId || '');
    setBio('');
    setFiles([]);
    setVideoLink('');
    setIsConsented(false);
    setSubmitStatus('idle');
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const canSubmit = selectedRoleId && isConsented && submitStatus !== 'submitting';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitStatus('submitting');
    setErrorMessage(null);
    try {
      await submitAuditionApplication(auditionId, { roleId: selectedRoleId, bio, videoLink, files, talentProfileId: profileId });
      setSubmitStatus('idle');
      onSuccess(selectedRoleId);
    } catch (err) {
      const status = err?.response?.status;
      // 409 = 이미 지원함(중복 클릭 포함) → 지원된 상태이므로 성공으로 본다.
      // 예전엔 중복도 500으로 와서 500까지 성공 처리했지만, 이제 500은 진짜 실패다.
      if (status === 409) {
        setSubmitStatus('idle');
        onSuccess(selectedRoleId);
        return;
      }
      if (status === 400) {
        const code = err?.response?.data?.code;
        const msg = err?.response?.data?.message || '지원 조건을 충족하지 않습니다.';
        setSubmitStatus('idle');
        if (code === 'GENDER_NOT_ELIGIBLE') {
          setConditionAlert({ title: '지원 불가', message: msg });
        } else if (code === 'AGE_NOT_ELIGIBLE') {
          setConditionAlert({ title: '나이 조건을 충족하지 않습니다', message: msg });
        } else {
          setConditionAlert({ title: '지원 불가', message: msg });
        }
        return;
      }
      setSubmitStatus('error');
      // 서버 응답이 있으면 서버 문구를, 없으면(네트워크·S3 업로드 실패) 던져진 문구를 쓴다
      setErrorMessage(
        (err?.response ? err.response.data?.message : err?.message)
          || '지원 중 오류가 발생했습니다. 다시 시도해 주세요.',
      );
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      {conditionAlert && (
        <RoleOnlyModal
          title={conditionAlert.title}
          message={conditionAlert.message}
          onClose={() => setConditionAlert(null)}
        />
      )}
      {ReactDOM.createPortal(
        <div
          className={styles.overlay}
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="apply-modal-title"
        >
          <div className={styles.modal}>
            <ApplyModalHeader onClose={onClose} auditionTitle={auditionTitle} />
            <ApplyModalBody
              roles={roles}
              selectedRoleId={selectedRoleId}
              onRoleSelect={handleRoleSelect}
              bio={bio}
              onBioChange={setBio}
              files={files}
              onFilesChange={setFiles}
              videoLink={videoLink}
              onVideoLinkChange={setVideoLink}
            />
            {errorMessage && (
              <p className={styles.errorMessage}>{errorMessage}</p>
            )}
            <ApplyModalFooter
              onClose={onClose}
              onSubmit={handleSubmit}
              isSubmitting={submitStatus === 'submitting'}
              isSubmitDisabled={!canSubmit}
              isConsented={isConsented}
              onConsentChange={setIsConsented}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default ApplyModal;
